import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Grid } from '@mui/material';
import moment, { Moment } from 'moment';

import { CfsApi, MasterNameApi } from '@/api';
import {
  DateTimePicker,
  FormCard,
  PrimaryReporterForm,
  RenderFormField,
} from '@/components';
import { RapidSOSAdditionalDataModal } from '@/components/Modals';
import { useCFSContext } from '@/hooks';
import {
  IFormOnChangeProps,
  ICFSForm,
  IMasterName,
  IMasterNameReq,
  MasterNameInvolvement,
  MasterNameType,
  PhoneNumberType,
} from '@/models';
import { isCFSViewOnly } from '@/services';
import { useToastStore, useUserStore } from '@/store';
import {
  getMasterNamePhoneNumbersByPriority,
  setPrimaryPhoneNumber,
} from '@/utils';

import { ReporterReceivedByField } from './data';
import { ReporterContactPhoneField } from './ReporterContactPhoneField';

export const ReporterForm = ({ onChange, ...props }: IFormOnChangeProps) => {
  const urlParams = useParams();
  const cfsId = props?.cfsId || String(urlParams.cfsId);
  const { getValues, setValue } = useFormContext<ICFSForm>();
  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const { cfs, ablyCfsChannel, updateCFS, fetchCFS } = useCFSContext();
  const readOnly = isCFSViewOnly(user);
  const masterName = cfs?.primaryReporter;
  const [capturedPhoneNumber, setCapturedPhoneNumber] = useState('');
  const [capturedReporterId, setCapturedReporterId] = useState('');
  const { primaryPhoneNumber, additionalPhoneNumbers } =
    getMasterNamePhoneNumbersByPriority(masterName);
  const isContactPhoneReadOnly = Boolean(
    primaryPhoneNumber ||
      (capturedReporterId === masterName?._id && capturedPhoneNumber),
  );

  useEffect(() => {
    setCapturedPhoneNumber('');
    setCapturedReporterId('');
  }, [masterName?._id]);

  useEffect(() => {
    const nextPhoneNumber = primaryPhoneNumber?.inputValue;
    if (
      !nextPhoneNumber ||
      getValues('reporter.contactPhoneNumber') === nextPhoneNumber
    ) {
      return;
    }

    setValue('reporter.contactPhoneNumber', nextPhoneNumber);
  }, [getValues, primaryPhoneNumber?.inputValue, setValue]);

  const syncReporterContactPhone = (reporter?: IMasterName) => {
    const { primaryPhoneNumber: nextPrimaryPhoneNumber } =
      getMasterNamePhoneNumbersByPriority(reporter);
    const nextPhoneNumber = nextPrimaryPhoneNumber?.inputValue || '';

    setCapturedPhoneNumber('');
    setCapturedReporterId('');
    setValue('reporter.contactPhoneNumber', nextPhoneNumber);

    return nextPhoneNumber;
  };

  const persistReporterContactPhone = async (nextPhoneNumber: string) => {
    if (!cfsId) return;

    await CfsApi.update(cfsId, {
      reporter: {
        contactPhoneNumber: nextPhoneNumber
          ? `+1${nextPhoneNumber}`
          : undefined,
      },
    });
  };

  /**
   * Resolve the reporter's contact phone when a person is linked. When the
   * dispatcher already typed a valid number it is PRESERVED (not overwritten by
   * the person's stored phone) and pushed onto the linked person's record as
   * their primary number, so it survives the re-derivation on refetch. Without a
   * typed value we keep the legacy auto-fill-from-person behavior.
   *
   * `personForState` carries the same phone change applied optimistically for
   * local state / the Ably payload. `personPutPromise` (when present) is the
   * background persist the caller awaits before force-refetching.
   */
  const resolveReporterContactPhone = async (
    reporter: IMasterName | undefined,
    typedContactPhone: string,
  ): Promise<{
    phoneNumber: string;
    personForState?: IMasterName;
    personPutPromise?: Promise<unknown>;
  }> => {
    const typed = (typedContactPhone || '').trim();
    const isValidTyped = /^[2-9][0-9]{9}$/.test(typed);

    if (!isValidTyped) {
      return {
        phoneNumber: syncReporterContactPhone(reporter),
        personForState: reporter,
      };
    }

    // Preserve the dispatcher-entered number on the field.
    setCapturedPhoneNumber('');
    setCapturedReporterId('');
    setValue('reporter.contactPhoneNumber', typed);

    // Push the number onto the linked Master Index person. The `type` on the
    // autocomplete selection is unreliable (a person picked from the list can
    // arrive typed as ORGANIZATION), so we fetch the authoritative record — it
    // both confirms person-hood and gives us the person's real current phone
    // list, avoiding clobbering existing numbers from a slim selection.
    if (reporter?._id) {
      try {
        const person = (await MasterNameApi.getMasterName(reporter._id)).data;
        if (person?.firstName) {
          const nextPhoneNumbers = setPrimaryPhoneNumber(
            person.phoneNumbers,
            typed,
            PhoneNumberType.OTHER,
          );
          const changed =
            JSON.stringify(nextPhoneNumbers) !==
            JSON.stringify(person.phoneNumbers ?? []);

          return {
            phoneNumber: typed,
            personForState: { ...person, phoneNumbers: nextPhoneNumbers },
            personPutPromise: changed
              ? // Minimal partial update — the backend applies a per-key $set, so
                // we send only these fields and never round-trip populated refs.
                MasterNameApi.updateMasterName({
                  _id: person._id,
                  firstName: person.firstName,
                  lastName: person.lastName,
                  phoneNumbers: nextPhoneNumbers,
                } as IMasterNameReq)
              : undefined,
          };
        }
      } catch {
        // Not a person / lookup failed — fall through to preserve on the CFS.
      }
    }

    // Organization (or no id): preserve on the CFS only.
    return { phoneNumber: typed, personForState: reporter };
  };

  // Await the background push of the dispatcher number onto the person's record.
  // Retention wins: on failure the reporter is still linked and the number stays
  // on the CFS — we just surface the error. Returns whether it succeeded so the
  // caller can skip a force-refetch that would re-derive the person's old phone.
  const settleReporterPersonPush = async (
    personPutPromise?: Promise<unknown>,
  ): Promise<boolean> => {
    if (!personPutPromise) return true;
    try {
      await personPutPromise;
      return true;
    } catch (err: any) {
      updateToast({
        open: true,
        message: `Reporter linked, but saving the phone number to their record failed: ${err.message}`,
      });
      return false;
    }
  };

  const handleChangeReceivedAt = async (value: Moment | string | null) => {
    const v = moment(value).toISOString();
    try {
      ablyCfsChannel?.publish('cfsForm', { receivedAt: v });
      if (cfs) updateCFS({ ...cfs, receivedAt: v });
      await CfsApi.update(String(cfsId), { receivedAt: v });
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const handleContactPhoneChange = (
    fieldName: string,
    value: string | number,
  ) => {
    onChange(fieldName, value);

    if (
      fieldName !== 'reporter.contactPhoneNumber' ||
      primaryPhoneNumber ||
      typeof value !== 'string'
    ) {
      return;
    }

    if (/^[2-9][0-9]{9}$/.test(value) && masterName?._id) {
      setCapturedPhoneNumber(value);
      setCapturedReporterId(masterName._id);
      return;
    }

    if (!value) {
      setCapturedPhoneNumber('');
      setCapturedReporterId('');
    }
  };

  const handleUpdatePrimaryReporter = async (
    newPrimaryReporter?: IMasterName,
  ) => {
    // The contact-phone field is auto-filled with the OUTGOING reporter's
    // primary number (and locked read-only) whenever that reporter has one.
    // Only treat the field as dispatcher-entered — preserving it and pushing it
    // onto the newly linked person — when it DIFFERS from that auto-fill value.
    // Otherwise switching from a reporter who already has a phone would wrongly
    // copy their number onto the new person's record. Read before any await so
    // an interleaved re-render can't lose it.
    const outgoingAutoFill = primaryPhoneNumber?.inputValue || '';
    const currentContactPhone = String(
      getValues('reporter.contactPhoneNumber') || '',
    );
    const typedContactPhone =
      currentContactPhone !== outgoingAutoFill ? currentContactPhone : '';
    try {
      let primaryReporter: IMasterName | undefined;
      if (cfsId && cfs?.primaryReporter?._id && !newPrimaryReporter) {
        const cfsMasterName = await CfsApi.getCfsMasterName(
          cfsId,
          cfs.primaryReporter._id,
        );
        const updatedInvolvement =
          cfsMasterName.data.relationship?.involvement?.filter(
            (inv: MasterNameInvolvement) =>
              inv !== MasterNameInvolvement.CALLER,
          ) || [];

        await CfsApi.updateCfsMasterName(cfsId, cfs.primaryReporter._id, {
          relationship: {
            involvement: updatedInvolvement,
          },
        });

        if (updatedInvolvement.length === 0) {
          await CfsApi.deleteCfsMasterName(cfsId, cfs.primaryReporter._id);
        }

        const cfsNamesResponse = await CfsApi.getCfsMasterNames(cfsId);
        const nextCaller = cfsNamesResponse.data.find(
          (name) =>
            name.masterName?._id !== cfs.primaryReporter?._id &&
            name.relationship?.involvement?.includes(
              MasterNameInvolvement.CALLER,
            ),
        );

        const updatedCfs = { ...cfs };
        updatedCfs.primaryReporter = nextCaller?.masterName as
          | IMasterName
          | undefined;
        const nextPhoneNumber = syncReporterContactPhone(
          nextCaller?.masterName,
        );
        const persistPhonePromise =
          persistReporterContactPhone(nextPhoneNumber);
        updatedCfs.reporter = {
          ...updatedCfs.reporter,
          contactPhoneNumber: nextPhoneNumber
            ? `+1${nextPhoneNumber}`
            : undefined,
        };
        updateCFS(updatedCfs);

        ablyCfsChannel?.publish('cfsForm', {
          primaryReporter: nextCaller?.masterName || null,
          reporter: {
            contactPhoneNumber: nextPhoneNumber ? `+1${nextPhoneNumber}` : null,
          },
        });
        await persistPhonePromise;
        await fetchCFS({ force: true });
        return;
      } else if (newPrimaryReporter && cfsId) {
        // Ensure newPrimaryReporter has all required fields
        if (!('type' in newPrimaryReporter)) {
          newPrimaryReporter = {
            ...(newPrimaryReporter as IMasterName),
            type: MasterNameType.ORGANIZATION,
          };
        }

        const primaryReporterSelection = {
          ...(newPrimaryReporter as IMasterName),
          type:
            (newPrimaryReporter as IMasterName).type ||
            MasterNameType.ORGANIZATION,
        } as IMasterName;

        if (cfs?.primaryReporter?._id === primaryReporterSelection?._id) {
          if (cfs) {
            const {
              phoneNumber: nextPhoneNumber,
              personForState,
              personPutPromise,
            } = await resolveReporterContactPhone(
              primaryReporterSelection,
              typedContactPhone,
            );
            const reporterForState = personForState ?? primaryReporterSelection;
            const updatedCfs = {
              ...cfs,
              primaryReporter: JSON.parse(JSON.stringify(reporterForState)),
            };
            const persistPhonePromise =
              persistReporterContactPhone(nextPhoneNumber);
            updatedCfs.reporter = {
              ...updatedCfs.reporter,
              contactPhoneNumber: nextPhoneNumber
                ? `+1${nextPhoneNumber}`
                : undefined,
            };
            updateCFS(updatedCfs);
            await ablyCfsChannel?.publish('cfsForm', {
              primaryReporter: JSON.parse(JSON.stringify(reporterForState)),
              reporter: {
                contactPhoneNumber: nextPhoneNumber
                  ? `+1${nextPhoneNumber}`
                  : null,
              },
            });
            await persistPhonePromise;
            await settleReporterPersonPush(personPutPromise);
          }
          return;
        }

        try {
          await CfsApi.addCfsMasterName(
            cfsId,
            primaryReporterSelection?._id || '',
            {
              relationship: {
                behavior: 'N/A',
                involvement: [MasterNameInvolvement.CALLER],
              },
            },
          );
          primaryReporter = primaryReporterSelection;
        } catch (err: any) {
          updateToast({
            open: true,
            message: `Error adding CFS master name: ${err.message}`,
          });
          return;
        }

        if (cfs && primaryReporter) {
          const {
            phoneNumber: nextPhoneNumber,
            personForState,
            personPutPromise,
          } = await resolveReporterContactPhone(
            primaryReporter,
            typedContactPhone,
          );
          const reporterForState = personForState ?? primaryReporter;

          // Create a fresh copy of the CFS with primaryReporter
          const updatedCfs = {
            ...cfs,
            primaryReporter: JSON.parse(JSON.stringify(reporterForState)),
          };

          const persistPhonePromise =
            persistReporterContactPhone(nextPhoneNumber);
          updatedCfs.reporter = {
            ...updatedCfs.reporter,
            contactPhoneNumber: nextPhoneNumber
              ? `+1${nextPhoneNumber}`
              : undefined,
          };
          // Update local state FIRST
          updateCFS(updatedCfs);

          // Then publish to Ably channel
          await ablyCfsChannel?.publish('cfsForm', {
            primaryReporter: JSON.parse(JSON.stringify(reporterForState)),
            reporter: {
              contactPhoneNumber: nextPhoneNumber
                ? `+1${nextPhoneNumber}`
                : null,
            },
          });
          await persistPhonePromise;
          // Push the number onto the person BEFORE refetching so the re-derived
          // primary matches. If the push failed, skip the force-refetch — it
          // would re-derive the person's old phone and clobber the typed value.
          const personPushOk = await settleReporterPersonPush(personPutPromise);
          if (personPushOk) await fetchCFS({ force: true });
        }
      }
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  return (
    <FormCard
      title="Reporter information"
      sx={{
        '& .cardTitle': {
          padding: '5px 15px',
          minHeight: '50px',
          lineHeight: '40px',
          marginBottom: '-12px',
        },
      }}
    >
      <RapidSOSAdditionalDataModal />
      <Grid item xs={12}>
        <PrimaryReporterForm
          masterName={masterName}
          readOnly={readOnly}
          onChange={
            handleUpdatePrimaryReporter as (
              newMasterName?: IMasterName,
            ) => Promise<void>
          }
        />
      </Grid>
      <Grid container columnSpacing={2} sx={{ paddingLeft: '18px' }}>
        <Grid item xs={6} sx={{ mt: 2 }}>
          <DateTimePicker
            label="Received at"
            value={cfs?.receivedAt ? moment(cfs.receivedAt) : null}
            onAccept={handleChangeReceivedAt}
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={6} sx={{ mt: 2 }}>
          <ReporterContactPhoneField
            additionalPhoneNumbers={additionalPhoneNumbers}
            onChange={handleContactPhoneChange}
            readOnly={isContactPhoneReadOnly || readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <RenderFormField
            {...ReporterReceivedByField}
            handleChange={onChange}
            disabled={readOnly}
          />
        </Grid>
      </Grid>
    </FormCard>
  );
};
